/**
 * AI log - spec 08's generation ledger, read-only (FR-4.4).
 *
 * This is the screen that answers "why does the app say this member has used all
 * their plans today". Three fields do that work:
 *
 * - `classifier_verdict` - whether the free-text notes box was accepted. A
 *   rejection is FREE (`ai_requests_rejected_is_free` in the schema forbids
 *   `counts_against_quota` on a rejected row), which is invisible to the member:
 *   their counter does not move, so a rejected attempt looks like nothing
 *   happened.
 * - `counts_against_quota` - whether the attempt did move it. A provider error
 *   after an ACCEPTED request counts, and that is the case a member would
 *   reasonably dispute.
 * - `quota_date` - the Nepal calendar day the attempt was charged to (FR-4.3.5).
 *   A member near midnight can be three attempts "down" on a day they think is
 *   still yesterday.
 *
 * No prompt or response body exists anywhere to show: `ai_requests` has no column
 * for one, so nothing here can leak what a member typed beyond the classifier's
 * own reason, which is shown as-is and is admin copy only.
 */

import { useState } from "react";

import { DataTable, PrimaryCell, type Column } from "../components/DataTable";
import {
  Badge,
  Button,
  Card,
  ErrorNotice,
  EmptyState,
  Field,
  Input,
  LoadingRows,
  Page,
  Pagination,
  Select,
} from "../components/primitives";
import { fetchAiRequests, type AiRequestEntry } from "../lib/api/ai";
import type { Page as PageType } from "../lib/api/types";
import { useQuery } from "../lib/hooks";
import { formatCount, formatDateTime, orDash } from "../lib/format";

const PAGE_SIZE = 50;

/** The vocabulary `ai_requests.outcome` allows in the schema. */
const OUTCOMES = ["success", "classifier_rejected", "validation_failed", "provider_error", "rate_limited"];

export function AiLogScreen() {
  const [outcome, setOutcome] = useState("");
  const [userId, setUserId] = useState("");
  const [offset, setOffset] = useState(0);

  const query = useQuery<PageType<AiRequestEntry>>(
    () =>
      fetchAiRequests({
        outcome: outcome || undefined,
        user_id: userId.trim() || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
    [outcome, userId, offset],
  );
  const { data, error, loading, refreshing, reload } = query;

  const columns: Array<Column<AiRequestEntry>> = [
    {
      key: "when",
      header: "When",
      inline: true,
      render: (row) => formatDateTime(row.created_at),
    },
    {
      key: "member",
      header: "Member",
      render: (row) => (
        <PrimaryCell
          title={row.member_name ?? "Removed member"}
          // The uuid is the filter value, so it is shown rather than hidden.
          detail={<span className="mono">{row.user_id.slice(0, 8)}…</span>}
        />
      ),
    },
    {
      key: "request",
      header: "Request",
      render: (row) =>
        row.request_type === "week"
          ? "Whole week"
          : `Day ${row.target_day_index ?? "?"}`,
    },
    {
      key: "verdict",
      header: "Classifier",
      render: (row) => (
        // No reason text: it can quote the member's diet or health prose.
        <Badge tone={row.classifier_verdict === "accepted" ? "active" : "muted"}>
          {row.classifier_verdict}
        </Badge>
      ),
    },
    {
      key: "outcome",
      header: "Outcome",
      render: (row) => (
        <PrimaryCell
          title={<Badge tone={outcomeTone(row.outcome)}>{row.outcome.replace(/_/g, " ")}</Badge>}
          detail={row.error_message ?? undefined}
        />
      ),
    },
    {
      key: "quota",
      header: "Charged",
      render: (row) =>
        row.counts_against_quota ? (
          <PrimaryCell title="Yes" detail={row.quota_date} />
        ) : (
          <PrimaryCell title={<span className="text-meta">No</span>} detail="free attempt" />
        ),
    },
    {
      key: "model",
      header: "Model",
      render: (row) => <span className="mono text-small">{orDash(row.model)}</span>,
    },
    {
      key: "tokens",
      header: "Tokens",
      align: "right",
      numeric: true,
      render: (row) =>
        row.prompt_tokens === null && row.completion_tokens === null
          ? "-"
          : formatCount((row.prompt_tokens ?? 0) + (row.completion_tokens ?? 0)),
    },
    {
      key: "latency",
      header: "Latency",
      align: "right",
      numeric: true,
      render: (row) => (row.latency_ms === null ? "-" : `${formatCount(row.latency_ms)} ms`),
    },
  ];

  return (
    <Page>
      <Card>
        <div className="filters">
          <div style={{ width: 220 }}>
            <Field label="Outcome">
              {(id) => (
                <Select
                  id={id}
                  value={outcome}
                  onChange={(event) => {
                    setOutcome(event.target.value);
                    setOffset(0);
                  }}
                >
                  <option value="">All outcomes</option>
                  {OUTCOMES.map((value) => (
                    <option key={value} value={value}>
                      {value.replace(/_/g, " ")}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <div style={{ width: 300 }}>
            <Field label="Member id" hint="Paste a uuid from a member's page.">
              {(id) => (
                <Input
                  id={id}
                  className="input--mono"
                  value={userId}
                  placeholder="Paste a uuid"
                  onChange={(event) => {
                    setUserId(event.target.value);
                    setOffset(0);
                  }}
                />
              )}
            </Field>
          </div>

          <div className="row row--tight">
            <Button
              onClick={() => {
                setOutcome("");
                setUserId("");
                setOffset(0);
              }}
              disabled={!outcome && !userId}
            >
              Clear filters
            </Button>
            <Button onClick={reload} disabled={refreshing}>
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        </div>
      </Card>

      {error ? <ErrorNotice error={error} onRetry={reload} /> : null}

      <Card flush>
        {loading && !data ? (
          <LoadingRows rows={6} columns={6} />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title="No generation attempts match these filters."
            body={data?.message ?? "Members generate a plan from the Diet tab of the app."}
          />
        ) : (
          <>
            <DataTable columns={columns} rows={data.items} rowKey={(row) => row.id} />
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              noun="attempts"
              onChange={setOffset}
            />
          </>
        )}
      </Card>
    </Page>
  );
}

/** A refusal is not a failure - it is the classifier doing its job, and it is
 *  free. Red is reserved for the outcomes that cost the member something. */
function outcomeTone(outcome: string): "active" | "muted" | "danger" | "pending" {
  if (outcome === "success") return "active";
  if (outcome === "classifier_rejected") return "muted";
  if (outcome === "rate_limited") return "pending";
  return "danger";
}
